import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productService, cartService, wishlistService } from '../../services/api';
import { ShoppingCart, Heart, ChevronLeft, Star, ShieldCheck, Truck, RefreshCcw } from 'lucide-react';
import AdSlot from '../AdSlot';
import { useAuth } from '../../contexts/AuthContext';
import { updateMetaTags } from '../../utils/seo';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadProduct();
  }, [slug]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const data = await productService.show(slug);
      setProduct(data);
      if (data.variants && data.variants.length > 0) {
        setSelectedVariant(data.variants[0]);
        if (data.variants[0].image) {
          setActiveImage(data.variants[0].image);
        } else {
          setActiveImage(data.featured_image || (data.images && data.images[0]));
        }
      } else {
        setActiveImage(data.featured_image || (data.images && data.images[0]));
      }
      
      updateMetaTags({
        title: data.name,
        description: data.description,
        image: data.featured_image,
        siteName: 'Ortus FMCG'
      });
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    try {
      const data = await cartService.addItem(
        product.id,
        quantity,
        selectedVariant?.id,
        selectedVariant ? { variant_name: selectedVariant.name } : null
      );
      setMessage({ type: 'success', text: 'Added to cart successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to add to cart.' });
    }
  };

  const handleAddToWishlist = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    try {
      await wishlistService.store(product.id);
      setMessage({ type: 'success', text: 'Added to wishlist!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add to wishlist.' });
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center min-h-[60vh]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
    </div>
  );

  if (!product) return (
    <div className="text-center py-20">
      <h2 className="text-2xl font-bold">Product not found</h2>
      <button onClick={() => navigate('/shop')} className="mt-4 text-red-600 flex items-center justify-center gap-2 mx-auto">
        <ChevronLeft size={20} /> Back to Shop
      </button>
    </div>
  );

  const currentPrice = selectedVariant ? (selectedVariant.discount_price || selectedVariant.price) : (product.discount_price || product.price);
  const oldPrice = selectedVariant ? (selectedVariant.discount_price ? selectedVariant.price : null) : (product.discount_price ? product.price : null);
  const variantStock = selectedVariant ? selectedVariant.stock : product.stock;

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <button 
        onClick={() => navigate('/shop')}
        className="mb-8 flex items-center gap-2 text-slate-500 hover:text-red-600 transition-colors"
      >
        <ChevronLeft size={20} />
        Back to Catalog
      </button>

      {message && (
        <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' ? 'bg-red-50 text-red-700' : 'bg-rose-50 text-rose-700'
        }`}>
          <div className={`w-2 h-2 rounded-full ${message.type === 'success' ? 'bg-red-500' : 'bg-rose-500'}`} />
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Product Gallery */}
        <div className="space-y-4">
          <div className="aspect-square bg-slate-100 rounded-2xl overflow-hidden border">
            <img 
              src={activeImage || '/placeholder-product.png'} 
              alt={product.name}
              className="w-full h-full object-cover transition-transform hover:scale-110 duration-500"
            />
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {[product.featured_image, ...(product.images || [])].filter(Boolean).map((img, idx) => (
              <button 
                key={idx}
                onClick={() => setActiveImage(img)}
                className={`w-20 h-20 rounded-lg overflow-hidden border-2 flex-shrink-0 ${
                  activeImage === img ? 'border-red-600' : 'border-transparent'
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Product Info */}
        <div className="flex flex-col">
          <div className="mb-2">
            <span className="text-red-600 font-medium text-sm uppercase tracking-wider">
              {product.category?.name}
            </span>
          </div>
          <h1 className="text-4xl font-black text-slate-900 mb-4">{product.name}</h1>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-1 text-amber-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} fill={i < Math.floor(product.rating || 5) ? 'currentColor' : 'none'} />
              ))}
            </div>
            <span className="text-slate-400 text-sm">({product.rating_count || 0} reviews)</span>
          </div>

          <div className="text-3xl font-bold text-slate-900 mb-6 flex items-baseline gap-3">
            ₹{currentPrice}
            {oldPrice && <span className="text-xl font-normal text-slate-400 line-through">₹{oldPrice}</span>}
          </div>

          <p className="text-slate-600 mb-8 leading-relaxed">
            {product.description}
          </p>

          {/* Variants */}
          {product.variants && product.variants.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-5">
                <div className="h-5 w-1 bg-gradient-to-b from-red-500 to-orange-500 rounded-full"></div>
                <label className="text-sm font-black text-slate-900 uppercase tracking-wider">
                  Select Option
                </label>
                {selectedVariant && (
                  <span className="ml-auto text-xs text-slate-400">
                    <span className="font-bold text-slate-700">{selectedVariant.name}</span>
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2.5">
                {product.variants.map((variant) => {
                  const isSelected = selectedVariant?.id === variant.id;
                  const isOutOfStock = variant.stock !== undefined && variant.stock <= 0;
                  return (
                    <button
                      key={variant.id}
                      onClick={() => {
                        setSelectedVariant(variant);
                        if (variant.image) {
                          setActiveImage(variant.image);
                        } else {
                          setActiveImage(product.featured_image || (product.images && product.images[0]));
                        }
                      }}
                      disabled={isOutOfStock}
                      className={`group relative flex flex-col items-center gap-2 w-[110px] px-3 pt-3 pb-2.5 rounded-2xl border transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                        isSelected
                          ? 'border-red-500 bg-gradient-to-br from-red-50 to-orange-50 shadow-lg shadow-red-100/50 ring-2 ring-red-500/20'
                          : 'border-slate-150 bg-white hover:border-slate-300 hover:shadow-md'
                      }`}
                    >
                      {variant.image ? (
                        <div className={`w-16 h-16 rounded-full overflow-hidden ring-2 transition-all ${isSelected ? 'ring-red-400 ring-offset-2' : 'ring-slate-200 group-hover:ring-slate-300'}`}>
                          <img src={variant.image} alt={variant.name} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center text-xs font-black uppercase transition-all ${isSelected ? 'bg-red-100 text-red-600 ring-2 ring-red-400 ring-offset-2' : 'bg-slate-100 text-slate-400'}`}>
                          {variant.name.charAt(0)}
                        </div>
                      )}
                      <div className={`text-center text-xs font-bold leading-tight ${isSelected ? 'text-red-700' : 'text-slate-600'}`}>
                        {variant.name}
                      </div>
                      <div className="text-[10px] font-medium">
                        {variant.discount_price ? (
                          <span className="text-red-600 font-bold">₹{variant.discount_price}</span>
                        ) : variant.price ? (
                          <span className="text-slate-400">₹{variant.price}</span>
                        ) : null}
                        {isOutOfStock && (
                          <span className="text-rose-500 block">Out of stock</span>
                        )}
                      </div>
                      {isSelected && (
                        <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center shadow">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Stock Status */}
          <div className="mb-4 text-sm font-bold">
            {variantStock !== undefined && variantStock <= 0 ? (
              <span className="text-rose-500">Out of Stock</span>
            ) : variantStock !== undefined && variantStock <= 5 ? (
              <span className="text-amber-500">Only {variantStock} left in stock!</span>
            ) : (
              <span className="text-green-500">In Stock</span>
            )}
          </div>

          {/* Quantity & Actions */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8 mt-auto">
            <div className="flex items-center border-2 border-slate-100 rounded-xl px-4 h-14">
              <button 
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-colors"
              >
                -
              </button>
              <span className="w-12 text-center font-bold text-slate-900">{quantity}</span>
              <button 
                onClick={() => setQuantity(quantity + 1)}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-colors"
              >
                +
              </button>
            </div>
            <button 
              onClick={handleAddToCart}
              disabled={variantStock !== undefined && variantStock <= 0}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl h-14 font-bold flex items-center justify-center gap-3 transition-colors shadow-lg shadow-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShoppingCart size={20} />
              Add to Cart
            </button>
            <button 
              onClick={handleAddToWishlist}
              className="w-14 h-14 border-2 border-slate-100 rounded-xl flex items-center justify-center text-slate-400 hover:text-rose-500 hover:border-rose-100 hover:bg-rose-50 transition-all"
            >
              <Heart size={20} />
            </button>
          </div>

          {/* Features */}
          <div className="grid grid-cols-3 gap-4 pt-8 border-t">
            <div className="text-center">
              <div className="w-10 h-10 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <ShieldCheck size={20} />
              </div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Pure & Safe</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <Truck size={20} />
              </div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Fast Delivery</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <RefreshCcw size={20} />
              </div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Easy Returns</p>
            </div>
          </div>
        </div>
      </div>

      {/* Product Detail Ad Slot */}
      <div className="mt-16 pt-8 border-t">
        <AdSlot position="product_bottom" className="h-[200px]" />
      </div>
    </div>
  );
};

export default ProductDetail;
